import { Divider, Grid } from '@approvalmax/ui/src/components';
import moment from 'moment';
import { memo, useMemo } from 'react';
import { PaymentDetailsAudit, PaymentDetailsBankAccountInfo, PaymentDetailsStatus } from 'shared/components';
import {
    CheckConfirmationOfPayee,
    PaymentDetailsBankAccountInfo as PaymentDetailsBankAccountInfoV2,
} from 'shared/components/(payments)';
import { noValue } from 'shared/constants';
import { useGetBillRequestPaymentDetails, useGetXeroCounterpartyPaymentDetailsForRequest } from 'shared/data/webApp/v1';
import {
    ApiAmaxPayPaymentDetailsVerificationStatus,
    useGetPaymentDetailsForBill,
    useGetPaymentDetailsForRequest,
} from 'shared/data/webApp/v2';
import { useCompanyBetaFeatures } from 'shared/hooks';

import { PaymentDetailsInfoProps } from './PaymentDetailsInfo.types';

export const PaymentDetailsInfo = memo<PaymentDetailsInfoProps>((props) => {
    const {
        status,
        companyId,
        requestId,
        billRequestId,
        counterpartyId,
        paymentDetailsId,
        skipBillRequest,
        paymentDetailsDiscrepancy,
        paymentDetailsDiscrepancyV2,
    } = props;

    const { isWalletsUK, isConfirmationOfPayee } = useCompanyBetaFeatures(companyId);

    const showV2Version = isWalletsUK || isConfirmationOfPayee;

    const { data: billRequestPaymentDetails, isFetching: isFetchingGetBillRequestPaymentDetails } =
        useGetBillRequestPaymentDetails(
            {
                path: {
                    companyId,
                    requestId,
                    billRequestId,
                },
            },
            {
                enabled:
                    !showV2Version &&
                    !paymentDetailsDiscrepancy?.paymentDetailsDiscrepancy.lastUsedPaymentDetailsProperties &&
                    // skip request until batch is created
                    !skipBillRequest,
            }
        );

    const { data: billRequestPaymentDetailsV2, isFetching: isFetchingGetBillRequestPaymentDetailsV2 } =
        useGetPaymentDetailsForBill(
            {
                path: {
                    companyId,
                    requestId,
                    billRequestId,
                },
            },
            {
                enabled:
                    showV2Version &&
                    // skip request until batch is created
                    !skipBillRequest,
            }
        );

    const {
        data: xeroCounterpartyPaymentDetailsForRequest,
        isFetching: isFetchingGetXeroCounterpartyPaymentDetailsForRequest,
    } = useGetXeroCounterpartyPaymentDetailsForRequest(
        {
            path: {
                companyId,
                requestId,
                counterpartyId,
            },
        },
        {
            enabled: !showV2Version,
        }
    );

    const {
        data: xeroCounterpartyPaymentDetailsForRequestV2,
        isFetching: isFetchingGetXeroCounterpartyPaymentDetailsForRequestV2,
    } = useGetPaymentDetailsForRequest(
        {
            path: {
                companyId,
                requestId,
                counterpartyId,
            },
        },
        {
            enabled: showV2Version,
        }
    );

    const isFetching =
        isFetchingGetBillRequestPaymentDetails ||
        isFetchingGetXeroCounterpartyPaymentDetailsForRequest ||
        isFetchingGetBillRequestPaymentDetailsV2 ||
        isFetchingGetXeroCounterpartyPaymentDetailsForRequestV2;

    const paymentDetailsByCounterpartyId = useMemo(
        () => xeroCounterpartyPaymentDetailsForRequest?.items.find((item) => item.id === paymentDetailsId),
        [xeroCounterpartyPaymentDetailsForRequest, paymentDetailsId]
    );

    const paymentDetailsByCounterpartyIdV2 = useMemo(
        () => xeroCounterpartyPaymentDetailsForRequestV2?.items.find((item) => item.id === paymentDetailsId),
        [xeroCounterpartyPaymentDetailsForRequestV2, paymentDetailsId]
    );

    const bankAccountDetails =
        billRequestPaymentDetails?.data.bankAccountDetails || paymentDetailsByCounterpartyId?.bankAccount;

    const normalizedPaymentDetailsAudit = useMemo(() => {
        const createdBy =
            billRequestPaymentDetails?.data.createdBy ||
            billRequestPaymentDetailsV2?.createdBy ||
            paymentDetailsByCounterpartyId?.createdBy ||
            paymentDetailsByCounterpartyIdV2?.createdBy ||
            noValue;
        const createdAtValue =
            billRequestPaymentDetails?.data.createdAt ||
            billRequestPaymentDetailsV2?.createdAt ||
            paymentDetailsByCounterpartyId?.createdAt ||
            paymentDetailsByCounterpartyIdV2?.createdAt;

        const createdAt = createdAtValue ? moment(createdAtValue).format('DD MMM YYYY HH:mm') : noValue;

        const modifiedBy =
            billRequestPaymentDetails?.data.modifiedBy ||
            billRequestPaymentDetailsV2?.modifiedBy ||
            paymentDetailsByCounterpartyId?.modifiedBy ||
            paymentDetailsByCounterpartyIdV2?.modifiedBy;

        const modifiedAtValue =
            billRequestPaymentDetails?.data.modifiedAt ||
            billRequestPaymentDetailsV2?.modifiedAt ||
            paymentDetailsByCounterpartyId?.modifiedAt ||
            paymentDetailsByCounterpartyIdV2?.modifiedAt;

        const modifiedAt = modifiedAtValue ? moment(modifiedAtValue).format('DD MMM YYYY HH:mm') : noValue;

        return {
            createdBy,
            createdAt,
            modifiedBy,
            modifiedAt,
        };
    }, [
        billRequestPaymentDetails,
        paymentDetailsByCounterpartyId,
        billRequestPaymentDetailsV2,
        paymentDetailsByCounterpartyIdV2,
    ]);

    const verificationStatus =
        billRequestPaymentDetailsV2?.paymentDetails?.bankAccountDetails.validationAndVerification?.verificationStatus ||
        paymentDetailsByCounterpartyIdV2?.bankAccountDetails.validationAndVerification?.verificationStatus;

    return (
        <Grid gap={12} padding='16'>
            <PaymentDetailsStatus status={status} />

            {showV2Version ? (
                <PaymentDetailsBankAccountInfoV2
                    data={billRequestPaymentDetailsV2?.paymentDetails || paymentDetailsByCounterpartyIdV2}
                    isLoading={
                        isFetchingGetBillRequestPaymentDetailsV2 ||
                        isFetchingGetXeroCounterpartyPaymentDetailsForRequestV2
                    }
                    lastUsedPaymentDetailsProperties={
                        paymentDetailsDiscrepancyV2?.paymentDetailsDiscrepancy?.lastUsedPaymentDetailsProperties ||
                        billRequestPaymentDetailsV2?.lastUsedPaymentDetailsProperties
                    }
                    status={status}
                />
            ) : (
                <PaymentDetailsBankAccountInfo
                    status={status}
                    bankAccount={bankAccountDetails}
                    lastUsedPaymentDetailsProperties={
                        paymentDetailsDiscrepancy?.paymentDetailsDiscrepancy.lastUsedPaymentDetailsProperties ||
                        billRequestPaymentDetails?.data.lastUsedPaymentDetailsProperties
                    }
                />
            )}

            <Divider spacing='2' />

            {isFetching ? <PaymentDetailsAudit.Loading /> : <PaymentDetailsAudit {...normalizedPaymentDetailsAudit} />}

            {showV2Version &&
                verificationStatus &&
                verificationStatus !== ApiAmaxPayPaymentDetailsVerificationStatus.not_checked && (
                    <>
                        <Divider spacing='2' />

                        <CheckConfirmationOfPayee status={verificationStatus} />
                    </>
                )}
        </Grid>
    );
});

PaymentDetailsInfo.displayName = 'PaymentDetailsInfo';
